// storage/MapStore.js
import { makeAutoObservable, runInAction } from "mobx";
import { post } from "../common/requester";
import appStore from "./AppStore";

class MapStore {
  // map state
  center = { lat: 42.876760, lng: 74.602019 }; // Bishkek default
  zoom = 12;

  // query filters
  radiusKm = 10;
  selectedSportIds = [];

  // data
  locations = [];
  isLoading = false;
  error = null;

  // geo init
  isInitLoading = false;
  isInitialized = false;

  // UX / network
  debounceMs = 300;
  _timer = null;

  isDetailsLoading = false;
  details = {};
  companyDetails = {};
  sport = "";
  wheelEnabled = false;

  selectedLocations = [];
  detailsById = new Map();
  isDetailsLoadingById = new Map();

  // UI helper: focus panel (set from component)
  focusPanel = null;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  _distanceMeters(lat1, lng1, lat2, lng2) {
    const R = 6371000;
    const toRad = (v) => (v * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  }

  setCenter(lat, lng) {
    const rLat = Number(lat);
    const rLng = Number(lng);

    if (!Number.isFinite(rLat) || !Number.isFinite(rLng)) return;

    this.center = {
      lat: +rLat.toFixed(6),
      lng: +rLng.toFixed(6),
    };
  }

  setCenterFromMap(lat, lng) {
    const rLat = +lat.toFixed(6);
    const rLng = +lng.toFixed(6);

    const curLat = +this.center.lat.toFixed(6);
    const curLng = +this.center.lng.toFixed(6);

    const moved = this._distanceMeters(curLat, curLng, rLat, rLng);
    if (moved < 30) return;

    this.center = { lat: rLat, lng: rLng };
    this._debouncedReload();
  }

  setZoomFromMap(z) {
    if (this.zoom === z) return;
    this.zoom = z;
    this._debouncedReload();
  }

  clear() {
    this.locations = [];
    this.error = null;
  }

  _debouncedReload() {
    if (this._timer) clearTimeout(this._timer);
    this._timer = setTimeout(() => {
      this._timer = null;
      this.loadLocations();
    }, this.debounceMs);
  }

  async initFromLocation() {
    if (this.isInitialized || this.isInitLoading) return;

    this.isInitLoading = true;
    this.error = null;

    try {
      const res = await post("location", {});

      if (res) {
        const lat = Number(res.lat);
        const lon = Number(res.lon);

        runInAction(() => {
          if (Number.isFinite(lat) && Number.isFinite(lon)) {
            this.center = {
              lat: +lat.toFixed(6),
              lng: +lon.toFixed(6),
            };
          }

          if (res.country_id) {
            appStore.user.country_id = res.country_id;
          }

          if (res.language && !appStore.user.language) {
            appStore.user.language = res.language;
          }

          this.isInitialized = true;
        });
      } else {
        runInAction(() => {
          this.isInitialized = true;
        });
      }
    } catch (e) {
      runInAction(() => {
        this.error = e?.message || "Failed to load location";
        this.isInitialized = true;
      });
    } finally {
      runInAction(() => {
        this.isInitLoading = false;
      });
    }
  }

  async initAndLoad() {
    await this.initFromLocation();
    await this.loadLocations();
  }

  async loadLocations() {
    this.isLoading = true;
    this.error = null;

    try {
      const args = {
        center_lat: this.center.lat,
        center_lon: this.center.lng,
        radius_km: this.radiusKm,
        sport: this.sport,
      };

      const res = await post("search_companies", args);

      if (res && res.result === 0) {
        runInAction(() => {
          this.locations = res.companies || [];
          this.selectedLocations = [];
        });
      } else {
        runInAction(() => {
          this.locations = [];
        });
      }
    } catch (e) {
      runInAction(() => {
        this.error = e?.message || "Failed to load locations";
        this.locations = [];
      });
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  }

  setFocusPanel(fn) {
    this.focusPanel = fn;
  }

  _focus() {
    try {
      this.focusPanel?.();
    } catch (e) {}
  }

  async selectLocations(ids) {
    this.selectedLocations = ids;
    await this.loadDetailsMany(ids);
    this._focus();
  }

  async loadDetails(id) {
    if (!id) return;

    if (this.detailsById.has(id)) return;

    this.isDetailsLoadingById.set(id, true);
    this.error = null;

    try {
      const res = await post("get_location_details", { location_id: id });
      if (res && res.result === 0) {
        runInAction(() => {
          this.detailsById.set(id, res.details || {});
        });
      }
    } catch (e) {
      runInAction(() => {
        this.error = e?.message || "Failed to load details";
        this.detailsById.delete(id);
      });
    } finally {
      runInAction(() => {
        this.isDetailsLoadingById.set(id, false);
      });
    }
  }

  async loadDetailsMany(ids) {
    const missing = (ids || []).filter((id) => !this.detailsById.has(id));
    if (missing.length === 0) return;

    for (const id of missing) {
      await this.loadDetails(id);
    }
  }

  async loadCompanyInfo(_id) {
    const res = await post("get_company_details", { company_id: _id }).catch(() => {});
    if (res && res.result === 0) {
      runInAction(() => {
        this.companyDetails = res.details;
      });
    }
  }

  setProperty(key, val) {
    this[key] = val;
  }
}

const mapStore = new MapStore();
export default mapStore;